import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  useTheme,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  Paper,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomTextField from "../components/RTLTextField";
import notify from "../components/notify";
import axios from "axios";
import loginBackground from "../assets/loginBackground.png";
import EmailIcon from "@mui/icons-material/MailOutline";
import LockIcon from "@mui/icons-material/Lock";
import { useDispatch } from "react-redux";
import { login } from "../redux/slices/user/thunk";

const LoginScreen = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");

  // التحقق من وجود المستخدم في التخزين الدائم فقط
  const user = JSON.parse(localStorage.getItem("user"));

  const API_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1`;
  const lang = localStorage.getItem("i18nextLng") === "en" ? "en" : "ar";

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t("email_invalid"))
        .required(t("email_required")),
      password: Yup.string()
        .min(6, t("password_min"))
        .required(t("password_required")),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const data = {
          email: values.email.trim(),
          password: values.password,
        };
        const response = await dispatch(login({ data })).unwrap();

        console.log("response", response);
        const user = response.user;
        if (user?._id) {

          if (user?.role == 'client') {

            localStorage.setItem("user", JSON.stringify(user));
            localStorage.setItem("token", response.token);

            notify(t("login.success"), "success");
            navigate("/home");
          } else {
            notify(t("login.mustClient"), "success");

          }
        }
      } catch (error) {
        const msg = error.response?.data?.message || t("networkError");
        setLoginError(msg);
        notify(msg, "error");
      } finally {
        setLoading(false);
      }
    },
  });

  if (user) return <Navigate to="/home" replace />;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        bgcolor: "background.default",
        backgroundImage: { xs: "none", md: `url(${loginBackground})` },
        backgroundSize: "cover",
      }}
    >
      {/* Overlay للشاشات الكبيرة */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          inset: 0,
          background: theme.palette.primary.gradient,
          opacity: 0.7,
          zIndex: 1,
        }}
      />

      <Container
        maxWidth="xs"
        disableGutters // إزالة الحواف في الموبايل لملء العرض
        sx={{
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh", // الحاوية تأخذ كامل طول الشاشة
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: { xs: 0, sm: 4 }, // زوايا حادة في الموبايل
            bgcolor: "background.paper",
            flexGrow: { xs: 1, sm: 0 },
            margin: "auto",
            display: "flex",
            flexDirection: "column",
            justifyContent: { xs: "center", sm: "center" }, // توسيط المحتوى داخلياً
          }}
        >
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h4" fontWeight={800} gutterBottom>
              {t("Login")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("lovely to see you again.")}
            </Typography>
          </Box>

          {loginError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {loginError}
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit}>
            <CustomTextField
              fullWidth
              name="email"
              label={t("Email address")}
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              sx={{ mb: 2.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon style={{ width: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            <CustomTextField
              fullWidth
              name="password"
              label={t("Password")}
              type={showPassword ? "text" : "password"}
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon style={{ width: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* تم إزالة Checkbox الـ Remember Me مع الإبقاء على رابط نسيان كلمة المرور */}
            {/*   <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 4 }}>
              <Link
                href="/forgot-password"
                variant="body2"
                sx={{ fontWeight: 600, textDecoration: "none" }}
              >
                {t("Forgot Password?")}
              </Link>
            </Box> */}

            <Button
              fullWidth
              size="large"
              variant="contained"
              type="submit"
              disabled={loading}
              sx={{
                py: 2,
                borderRadius: 2,
                fontSize: "1rem",
                fontWeight: 700,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t("Login")
              )}
            </Button>

            <Typography variant="body2" align="center" sx={{ mt: 4 }}>
              {t("Don't have an account?")}{" "}
              <Link
                href="/signup"
                sx={{ fontWeight: 700, textDecoration: "none" }}
              >
                {t("Sign up")}
              </Link>
            </Typography>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginScreen;
